// Synthetic column keys injected client-side for the B2B revenue table.
// These are not sent by the backend — the FE derives them from the circular fields.
export const CIRCULAR_TXN_KEY = '__circularTxn';
export const NET_KEY = '__net';

// Backend key of the Total column (revenue table) — relabelled to "Total" + made sortable.
// (Same literal the overlap indicator used to key off — one source now.)
export const TOTAL_COLUMN_KEY = 'totalRevenue';

// Backend-sent summary rows are matched by name (their circular % renders as plain
// text, not a pill badge, per the design).
export const SUMMARY_ROW_NAMES = [
    'Total Revenue',
    'Total B2B Revenue',
    'Total Expense',
    'Net B2B Revenue',
    'Intra Company Txn.',
    'Total B2B Expense',
    'Net B2B Expense',
];

// Label of the synthetic "Related Parties" group row.
export const RELATED_GROUP_LABEL = 'Related Parties';

// Copy of the single synthetic row shown when the Related Parties group is open with zero children.
export const RELATED_EMPTY_LABEL = 'No related parties found';

export const PAGE_SIZE = 50;

// Accordion: which group's children are listed ('' = both collapsed).
export type OpenGroup = 'customers' | 'related' | '';
export const DEFAULT_OPEN_GROUP: OpenGroup = 'customers';

// Polus (issue #670) serializes isOverlapping as the string "true", not a boolean.
// Accept both forms so the gate stays correct if the serialization is ever fixed upstream.
export const OVERLAP_FLAG_STRING = 'true' as const;

// How long the tooltip stays visible after the cursor leaves the trigger (ms)
export const OVERLAP_TOOLTIP_HIDE_DELAY = 400;

export interface OverlapRow {
    isOverlapping?: string | boolean;
    circularValue?: string;
    circularPct?: string;
    circularTotal?: string;
    totalRevenue?: string;
    totalExpense?: string;
    relatedPartyRelation?: string;
    [key: string]: unknown;
}

/**
 * Gate — returns true when the row is flagged as overlapping.
 * Polus (#670) sends isOverlapping as the string "true"; accept a real boolean too.
 */
export function isOverlappingRow(row: OverlapRow): boolean {
    return row?.isOverlapping === OVERLAP_FLAG_STRING || row?.isOverlapping === true;
}

// Tooltip copy for the column-header info icons.
export const CIRCULAR_TXN_TOOLTIP = 'This party is customer as well vendor';
export const NET_COLUMN_TOOLTIP = 'Net revenue from this party';
export const ROW_TOOLTIPS: Record<string, string> = {
    'Net B2B Revenue': 'This is net B2B revenue after subtracting Intra Company Txn.',
    'Intra Company Txn.': "This amount is billed for the same company's GST number",
    'Net B2B Expense': 'This is net B2B expense after subtracting Intra Company Txn.',
};

// Per-tab config — one code path for the revenue and expense B2B tables, so the
// Circular/Net columns, totals-row relabel and name-hover copy stay symmetric by construction.
export interface OverlapTabConfig {
    totalKey: string;
    totalRowName: string;
    totalRowLabel: string;
    customersGroupLabel: string;
    netRowName: string;
    ownLabel: string;
    otherLabel: string;
    netColumnTooltip: string;
    nameHoverCopy: (amount: string, pct: string) => string;
}

export const OVERLAP_TAB_CONFIG: Record<'revenue' | 'expense', OverlapTabConfig> = {
    revenue: {
        totalKey: TOTAL_COLUMN_KEY,
        totalRowName: 'Total Revenue',
        totalRowLabel: 'Total B2B Revenue',
        customersGroupLabel: 'B2B Customers',
        netRowName: 'Net B2B Revenue',
        ownLabel: 'Revenue',
        otherLabel: 'Expense',
        netColumnTooltip: NET_COLUMN_TOOLTIP,
        nameHoverCopy: (amount, pct) => `This party is supplier as well with circular transaction of ${amount} (${pct}%)`,
    },
    expense: {
        totalKey: 'totalExpense',
        totalRowName: 'Total Expense',
        totalRowLabel: 'Total B2B Expense',
        customersGroupLabel: 'B2B Vendors',
        netRowName: 'Net B2B Expense',
        ownLabel: 'Expense',
        otherLabel: 'Revenue',
        netColumnTooltip: 'Net expense to this party',
        nameHoverCopy: (amount, pct) => `This party is customer as well with circular transaction of ${amount} (${pct}%)`,
    },
};

/**
 * Circular % → severity bucket (product-defined):
 * ≤10 yellow · >10–30 orange · >30–50 light red · >50 dark red.
 */
export function circularSeverity(pct?: string): 'yellow' | 'orange' | 'lightRed' | 'darkRed' {
    const n = pct ? parseFloat(String(pct)) : 0;
    if (n <= 10) return 'yellow';
    if (n <= 30) return 'orange';
    if (n <= 50) return 'lightRed';
    return 'darkRed';
}

/** Ensure the circular % renders with a trailing "%" (backend sends the number without it). */
export function formatCircularPct(pct?: string): string {
    if (pct === undefined || pct === null || pct === '') return '';
    const s = String(pct).trim();
    return s.endsWith('%') ? s : `${s}%`;
}

/** Parse a "₹12,34,567" string to a number. */
export function parseINR(value: string | undefined): number {
    if (!value) return 0;
    return parseFloat(value.replace(/[₹,]/g, '')) || 0;
}

/** Format a number as "₹12,34,567" (Indian grouping, no fraction). */
export function formatINR(value: number): string {
    return '₹' + value.toLocaleString('en-IN');
}

/** formatINR rounded to whole rupees (`|| 0` turns -0 into 0) — for this table's FE-computed amounts. */
export function formatWholeRupeesINR(value: number): string {
    return formatINR(Math.round(value) || 0);
}

/** Format a NUMBER in the selected unit (AUDITED_UNIT_DIVISOR). Divisor 1 (INR) → whole rupees;
 *  Lakhs/Crore → "₹" + en-IN 2-dp, mirroring AuditedStatementTable's rescaleValue. */
export function formatAmountInUnit(value: number, unitDivisor = 1): string {
    if (unitDivisor === 1) return formatWholeRupeesINR(value);
    return '₹' + (value / unitDivisor).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Display-only rescale of a backend-formatted "₹…" string into the selected unit. Divisor 1 (INR)
 *  returns the value untouched. Non-₹ values ('-', names, states, %) pass through. */
export function scaleINR(value: string, divisor?: number): string;
export function scaleINR(value: unknown, divisor?: number): unknown;
export function scaleINR(value: unknown, divisor = 1): unknown {
    if (divisor === 1 || typeof value !== 'string' || !value.includes('₹')) return value;
    return formatAmountInUnit(parseINR(value), divisor);
}

/**
 * True when a table cell holds a NEGATIVE rupee amount.
 * Gated on '₹' so non-amount columns (state, names, % numbers) can never match, and
 * routed through parseINR so the empty-cell placeholder '-' parses to 0, not a negative
 * (`parseFloat('-')` → NaN → `|| 0`). A naive startsWith('-')/includes('-') is inverted
 * for this table: our negatives read "₹-12,03,750" (minus AFTER the ₹) while '-' means EMPTY.
 */
export function isNegativeAmount(value: unknown): boolean {
    if (typeof value !== 'string' || !value.includes('₹')) return false;
    return parseINR(value) < 0;
}

/** True for a synthetic accordion group row (B2B Customers / Related Parties). */
export function isGroupRow(row: OverlapRow): boolean {
    return row?.__group === 'customers' || row?.__group === 'related';
}

/** True for the synthetic empty-state row under an open, childless Related Parties group. */
export function isEmptyGroupRow(row: OverlapRow): boolean {
    return row?.__emptyGroup === 'related';
}

/** True when the row is a backend-sent summary row (Total Revenue, Net B2B Revenue, …). */
export function isSummaryRow(row: OverlapRow): boolean {
    return typeof row?.name === 'string' && SUMMARY_ROW_NAMES.includes(row.name as string);
}

/**
 * Net = Total − Circular, keyed off the row's DISPLAYED own-side total (`totalKey`), not circularTotal.
 * circularTotal is max(rev, exp) upstream — for a party whose expense exceeds its revenue that
 * would render exp − rev (which can exceed the displayed Total); revenue − circular correctly
 * yields 0 when the entire revenue is circular. Returns '-' when the row carries no circular data.
 */
export function getNetValue(row: OverlapRow, totalKey: string = TOTAL_COLUMN_KEY): string {
    if (!isOverlappingRow(row) || !row?.[totalKey] || !row?.circularValue) return '-';
    return formatWholeRupeesINR(parseINR(row[totalKey] as string) - parseINR(row.circularValue));
}

/**
 * Circular % of the row's DISPLAYED total (circularValue / own-side total) — the single
 * denominator the badge, severity colour, sort, and tooltip all share. The backend's
 * circularPct is min/max-based, which diverges from the displayed Total when a party's
 * expense exceeds its revenue (badge said 40% while the tooltip's amounts implied 100%);
 * own-side-total-based reads as "share of this party's own-side total that is circular" and is ≤100 by
 * construction (circularValue = min(rev, exp) ≤ own-side total). Falls back to the backend
 * circularPct when the totals needed to recompute are absent.
 */
export function circularRevenuePct(row: OverlapRow, totalKey: string = TOTAL_COLUMN_KEY): string | undefined {
    if (!isOverlappingRow(row)) return undefined;
    const total = parseINR(row[totalKey] as string);
    const circ = parseINR(row.circularValue);
    if (!total || !circ) return row?.circularPct;
    return String(Math.round((circ / total) * 1000) / 10);
}

/**
 * The other-side total for the tooltip — FE-derived, no new backend field.
 * `circularValue` = min(own, other) and `circularTotal` = max(own, other): whichever one equals
 * the row's own displayed total is the OWN side, so the other one is the other-side total.
 */
export function counterpartTotal(row: OverlapRow, totalKey: string = TOTAL_COLUMN_KEY): string | undefined {
    return parseINR(row.circularValue) === parseINR(row[totalKey] as string) ? row.circularTotal : row.circularValue;
}

// Optional row key on GST "Vendor Expense" rows: the vendor PAN when it is in the org's latest MSME delay period.
export const MSME_DELAY_PAN_KEY = 'msmeDelayPan';
export const MSME_DELAY_TAG_LABEL = 'MSME Delay';
export const msmeDelayTagAriaLabel = (vendorName: string) => `${MSME_DELAY_TAG_LABEL} — ${vendorName}`;
export const MSME_DELAY_LOAD_ERROR = 'Unable to load MSME delay details. Please try again.';
